import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { focusRing } from "@/lib/focus-ring";
import { controlMotion, controlPress } from "@/lib/motion-classes";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  `font-ui inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[var(--r-btn)] border text-sm font-medium shadow-none ${controlMotion} ${controlPress} disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55 aria-invalid:border-destructive aria-invalid:ring-destructive/30 ${focusRing} [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
  {
    variants: {
      variant: {
        accent:
          "border-transparent bg-primary text-primary-foreground hover:bg-[color:var(--zom-antique-bronze-hover)]",
        primary:
          "border-transparent bg-primary text-primary-foreground hover:bg-[color:var(--zom-antique-bronze-hover)]",
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-[color:var(--zom-antique-bronze-hover)]",
        destructive:
          "border-transparent bg-destructive text-[var(--zom-public-white)] hover:bg-destructive/90 hover:text-[var(--zom-public-white)] focus-visible:ring-destructive/40",
        outline:
          "border-border bg-card text-foreground hover:border-[color:var(--zom-fine-rule)] hover:bg-background-muted",
        secondary:
          "border-border bg-secondary text-secondary-foreground hover:border-[color:var(--zom-fine-rule)] hover:bg-background-muted",
        ghost:
          "border-transparent bg-transparent text-foreground hover:border-border hover:bg-background-muted",
        subtle:
          "border-border bg-background-muted text-secondary-foreground hover:border-[color:var(--zom-fine-rule)] hover:bg-surface-sunken",
        link: "border-transparent bg-transparent text-[color:var(--zom-accent-text)] underline-offset-4 hover:underline",
        /* Flow-canonical variants: read --fc-* tokens, so they are only
           valid inside a [data-layout="flow-canonical"] / .fc-root subtree
           (settings, ai-chat, onboarding v2, workspaces, the UI kit).
           Outside that scope the vars are undefined and the button renders
           unstyled. */
        "fc-outline":
          "rounded-lg border-[color:var(--fc-text-muted)] bg-[color:var(--fc-card-bg)] text-[color:var(--fc-text)] hover:bg-[color:var(--fc-inline-bg)] hover:text-[color:var(--fc-text)] focus-visible:ring-[color:var(--fc-accent)]/35 focus-visible:ring-offset-[color:var(--fc-chat-bg)]",
        "fc-primary":
          "rounded-lg border-[color:var(--fc-button-primary)] bg-[color:var(--fc-button-primary)] text-[color:var(--fc-button-primary-fg)] shadow-[var(--fc-shadow-btn-primary)] hover:bg-[color:var(--fc-button-primary-hover)] hover:text-[color:var(--fc-button-primary-fg)] focus-visible:ring-[color:var(--fc-accent)]/35 focus-visible:ring-offset-[color:var(--fc-chat-bg)]",
        "fc-ghost":
          "rounded-lg border-transparent bg-transparent text-[color:var(--fc-accent)] hover:bg-[color:var(--fc-inline-bg)] hover:text-[color:var(--fc-accent)] focus-visible:ring-[color:var(--fc-accent)]/35 focus-visible:ring-offset-[color:var(--fc-chat-bg)]",
        "fc-destructive":
          "rounded-[var(--fc-radius-button)] border-[color:var(--fc-tone-danger-border)] bg-[color:var(--fc-tone-danger-bg)] text-[color:var(--fc-tone-danger-fg)] hover:border-[color:var(--fc-danger)] hover:bg-[color:var(--fc-tone-danger-bg)] focus-visible:ring-[color:var(--fc-danger)]/40 focus-visible:ring-offset-[color:var(--fc-chat-bg)]",
        /* Workspace page variants (September reference). They read --ws-*
           tokens, defined on every flow-canonical scope, and draw focus as the
           reference does: a 2px text-ink outline 3px off the control, with no
           ring. Pair them with the ws-* sizes.
           ws-primary   #25262a fill and border, white label, hover #3d3d43.
           ws-secondary white, #e3e3e6 control border, text ink; hover
                        #f7f7f8 fill and #c7c8cc border.
           ws-quiet     a text action: no fill, no border, no side padding,
                        #3d3d43 regular-weight label, text ink and underline
                        on hover. */
        "ws-primary":
          "rounded-[6px] border-[color:var(--ws-primary-bg)] bg-[color:var(--ws-primary-bg)] text-[color:var(--ws-on-primary)] hover:border-[color:var(--ws-primary-bg-hover)] hover:bg-[color:var(--ws-primary-bg-hover)] hover:text-[color:var(--ws-on-primary)] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[color:var(--ws-focus)] focus-visible:outline-solid",
        "ws-secondary":
          "rounded-[6px] border-[color:var(--ws-control-border)] bg-[color:var(--ws-card-bg)] text-[color:var(--ws-ink)] hover:border-[color:var(--ws-control-border-hover)] hover:bg-[color:var(--ws-subtle-bg)] hover:text-[color:var(--ws-ink)] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[color:var(--ws-focus)] focus-visible:outline-solid",
        "ws-quiet":
          "rounded-[6px] border-transparent bg-transparent text-[color:var(--ws-ink-link)] underline-offset-[3px] hover:bg-transparent hover:text-[color:var(--ws-ink)] hover:underline focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[color:var(--ws-focus)] focus-visible:outline-solid",
      },
      size: {
        default: "h-9 px-4 has-[>svg]:px-3.5",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        xs: "h-7 gap-1 px-2.5 text-[11px] has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 px-5 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-xs": "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg": "size-10",
        /* Dom's product guide: 40px desktop target, 44px coarse-pointer target,
           14/20 regular text, 8px radius and 8px icon gap. */
        zom: "h-auto min-h-10 gap-2 rounded-lg px-4 py-2 text-sm leading-5 font-normal pointer-coarse:min-h-11",
        "icon-zom":
          "size-10 rounded-lg p-0 text-sm font-normal pointer-coarse:size-11 [&_svg:not([class*='size-'])]:size-4",
        /* Workspace sizes, measured from the reference. Height comes from
           padding plus a minimum so a wrapped label grows the control instead
           of clipping; touch pointers keep a 44px target.
           ws-sm  advisor .btn.compact  11px/1.35, 6px 10px, min 30, gap 7
           ws-md  household .button    11px/1.4, 7px 11px, min 32, gap 6
           ws-lg  advisor .btn         12px/1.35, 9px 13px, min 36, gap 7
           ws-op  operating .button    11px/1.55, 8px 12px, min 35, gap 8
           ws-gov governance/settings  12px/1.6, 7px 12px, min 34, gap 6
                  .button (35.2 tall with its border)
           Declared 540/550 draw the regular face, as in the reference. */
        "ws-sm":
          "h-auto min-h-[30px] gap-[7px] rounded-[6px] px-[10px] py-[6px] text-[11px] leading-[1.35] font-[540] pointer-coarse:min-h-11 [&_svg:not([class*='size-'])]:size-3.5",
        "ws-md":
          "h-auto min-h-[32px] gap-[6px] rounded-[6px] px-[11px] py-[7px] text-[11px] leading-[1.4] font-[550] pointer-coarse:min-h-11 [&_svg:not([class*='size-'])]:size-3.5",
        "ws-lg":
          "h-auto min-h-[36px] gap-[7px] rounded-[6px] px-[13px] py-[9px] text-[12px] leading-[1.35] font-[540] pointer-coarse:min-h-11 [&_svg:not([class*='size-'])]:size-3.5",
        "ws-op":
          "h-auto min-h-[35px] gap-2 rounded-[6px] px-3 py-2 text-[11px] leading-[1.55] font-[550] pointer-coarse:min-h-11 [&_svg:not([class*='size-'])]:size-3.5",
        "ws-gov":
          "h-auto min-h-[34px] gap-[6px] rounded-[6px] px-3 py-[7px] text-[12px] leading-[1.6] font-[550] pointer-coarse:min-h-11 [&_svg:not([class*='size-'])]:size-3.5",
      },
      shape: {
        default: "",
        pill: "rounded-full",
      },
    },
    compoundVariants: [
      {
        /* The reference's quiet action is a link: regular weight and no side
           padding, so its label lines up with the text around it. */
        variant: "ws-quiet",
        size: ["ws-sm", "ws-md", "ws-lg", "ws-op", "ws-gov"],
        className: "px-0 font-normal",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  shape,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, shape, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
